/**
 * The menu notice's dismiss countdown (CH-13b). The countdown starts once the notice has been on screen, not when
 * it is set: the change that raises a notice can stall the very next frame. A quality preset change recompiles the
 * shader programs on that frame, which took 5-6 s on headless WebGL2, so a wall-clock timer armed at the change ran
 * out before the notice was ever painted and the player (and the e2e menu spec) never saw it.
 *
 * Two animation frames: the first may run inside the frame that paints the notice (before or after the game's own
 * render), the second runs only after that frame has finished. UI events only, never per frame.
 */

/** The frame and timer functions the countdown uses (the browser's by default; tests pass a hand-stepped clock). */
export interface NoticeClock {
  requestAnimationFrame(cb: () => void): number
  cancelAnimationFrame(id: number): void
  setTimeout(cb: () => void, ms: number): number
  clearTimeout(id: number): void
}

export interface NoticeTimer {
  /** (Re)starts the countdown: `clear` runs `ms` after the notice has been painted. Drops any earlier countdown. */
  arm(clear: () => void): void
  /** Drops a pending countdown. */
  cancel(): void
}

export function createNoticeTimer(ms: number, clock: NoticeClock = window): NoticeTimer {
  let frame = 0
  let timer = 0
  const cancel = (): void => {
    clock.cancelAnimationFrame(frame)
    clock.clearTimeout(timer)
  }
  return {
    arm(clear: () => void): void {
      cancel()
      frame = clock.requestAnimationFrame(() => {
        frame = clock.requestAnimationFrame(() => {
          timer = clock.setTimeout(clear, ms)
        })
      })
    },
    cancel,
  }
}
